import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Megaphone, Sparkles, Users } from 'lucide-react';
import { API, toast } from '@/App';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { usePublicSeo } from '@/utils/seo';

const BENEFITS = [
  "Lien d'affiliation unique",
  'Commissions sur abonnements Coli Noki Pro',
  'Dashboard AMBASSADEUR avec statistiques agrégées',
  'Demandes de retrait et suivi des paiements',
];

const HOW_IT_WORKS = [
  'Vous candidatez publiquement au programme AMBASSADEUR.',
  "L’administration vérifie votre audience et votre profil.",
  'Après approbation, votre compte AMBASSADEUR est activé.',
  'Vous partagez votre lien et suivez vos commissions.',
];

const defaultFormState = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  phone: '',
  country: '',
  city: '',
  social_links_raw: '',
  audience_size: '',
  message: '',
};

const splitSocialLinks = (rawValue) =>
  String(rawValue || '')
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .slice(0, 10);

const AmbassadorProgramPage = () => {
  const location = useLocation();
  usePublicSeo({
    title: 'Programme AMBASSADEUR | Coli Noki',
    description:
      'Candidature publique au programme AMBASSADEUR Coli Noki, validation admin et commissions sur abonnements Pro.',
    path: '/ambassador-program',
  });

  const [formState, setFormState] = useState(defaultFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submissionState, setSubmissionState] = useState(null);
  const referralCode = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return String(params.get('ref') || '').trim().toUpperCase();
  }, [location.search]);

  const socialLinksPreview = useMemo(
    () => splitSocialLinks(formState.social_links_raw),
    [formState.social_links_raw]
  );

  useEffect(() => {
    if (!referralCode) {
      return;
    }
    const clickTrackKey = `cnk_affiliate_click_${referralCode}_${location.pathname}`;
    if (window.sessionStorage.getItem(clickTrackKey)) {
      return;
    }
    axios
      .post(`${API}/affiliate/track-click`, {
        affiliate_code: referralCode,
        landing_path: location.pathname,
      })
      .then(() => {
        window.sessionStorage.setItem(clickTrackKey, '1');
      })
      .catch(() => {
        // Ignore tracking failures on the public page.
      });
  }, [location.pathname, referralCode]);

  const updateField = (field, value) => {
    setFormState((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    const audienceSize = Number(formState.audience_size || 0);
    const socialLinks = splitSocialLinks(formState.social_links_raw);

    if (!socialLinks.length) {
      toast.error('Ajoutez au moins un lien social valide.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/ambassador-applications`, {
        first_name: formState.first_name.trim(),
        last_name: formState.last_name.trim(),
        email: formState.email.trim().toLowerCase(),
        password: formState.password,
        phone: formState.phone.trim(),
        country: formState.country.trim() || null,
        city: formState.city.trim() || null,
        social_links: socialLinks,
        audience_size: Number.isFinite(audienceSize) ? Math.max(0, Math.floor(audienceSize)) : 0,
        message: formState.message.trim() || null,
        referral_code: referralCode || null,
      });

      const data = response?.data?.data?.application || {};
      setSubmissionState(data);
      setFormState(defaultFormState);
      toast.success("Candidature envoyée. Elle sera évaluée par l’administration.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible d'envoyer la candidature.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1">
        <section className="bg-gradient-to-br from-red-50 via-white to-cyan-50 py-14">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-red-600 hover:bg-red-600 text-white mb-4">Programme AMBASSADEUR</Badge>
              {referralCode ? (
                <div className="mb-4">
                  <Badge variant="outline">Invitation super ambassadeur: {referralCode}</Badge>
                </div>
              ) : null}
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Devenez <span className="text-red-600">AMBASSADEUR</span> Coli Noki
              </h1>
              <p className="text-gray-700 text-lg">
                Recommandez Coli Noki à de nouveaux vendeurs, suivez vos performances et gagnez des commissions sur
                les abonnements Coli Noki Pro.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Megaphone className="w-5 h-5 text-red-600" />
                    Promotion
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-700">
                  Faites connaître la plateforme à votre audience via votre lien dédié.
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-cyan-600" />
                    Recrutement
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-700">
                  Les vendeurs inscrits via votre lien sont tracés de façon fiable par le backend.
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Commissions
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-700">
                  Vos commissions sont calculées uniquement sur abonnements Pro valides.
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="container mx-auto px-4 max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Avantages AMBASSADEUR</CardTitle>
                <CardDescription>Programme public externe à la population vendeurs.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {BENEFITS.map((item) => (
                  <div key={item} className="flex gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Comment ça fonctionne</CardTitle>
                <CardDescription>Validation obligatoire par l’administration.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {HOW_IT_WORKS.map((item, index) => (
                  <div key={item} className="flex items-start gap-3 text-sm text-gray-700">
                    <Badge variant="outline" className="min-w-7 justify-center">
                      {index + 1}
                    </Badge>
                    <span>{item}</span>
                  </div>
                ))}
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  Le programme AMBASSADEUR Coli Noki est réservé en priorité aux profils disposant d’au moins{' '}
                  <strong>10 000 abonnés</strong> ou d'une audience significative.
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="pb-16">
          <div className="container mx-auto px-4 max-w-5xl">
            <Card className="border-2 border-red-100">
              <CardHeader>
                <CardTitle>Candidature AMBASSADEUR</CardTitle>
                <CardDescription>
                  Votre candidature sera soumise avec le statut <strong>en attente</strong> jusqu'à validation admin.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amb-first-name">Prénom</Label>
                      <Input
                        id="amb-first-name"
                        value={formState.first_name}
                        onChange={(event) => updateField('first_name', event.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="amb-last-name">Nom</Label>
                      <Input
                        id="amb-last-name"
                        value={formState.last_name}
                        onChange={(event) => updateField('last_name', event.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amb-email">Email</Label>
                      <Input
                        id="amb-email"
                        type="email"
                        value={formState.email}
                        onChange={(event) => updateField('email', event.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="amb-phone">Telephone</Label>
                      <Input
                        id="amb-phone"
                        value={formState.phone}
                        onChange={(event) => updateField('phone', event.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="amb-country">Pays</Label>
                      <Input
                        id="amb-country"
                        value={formState.country}
                        onChange={(event) => updateField('country', event.target.value)}
                        placeholder="Congo"
                      />
                    </div>
                    <div>
                      <Label htmlFor="amb-city">Ville</Label>
                      <Input
                        id="amb-city"
                        value={formState.city}
                        onChange={(event) => updateField('city', event.target.value)}
                        placeholder="Brazzaville"
                      />
                    </div>
                    <div>
                      <Label htmlFor="amb-audience">Audience approximative</Label>
                      <Input
                        id="amb-audience"
                        type="number"
                        min="0"
                        step="1"
                        value={formState.audience_size}
                        onChange={(event) => updateField('audience_size', event.target.value)}
                        placeholder="10000"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="amb-password">Mot de passe</Label>
                    <Input
                      id="amb-password"
                      type="password"
                      value={formState.password}
                      onChange={(event) => updateField('password', event.target.value)}
                      minLength={8}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ce mot de passe sera utilisé pour votre connexion après approbation.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="amb-social-links">Liens reseaux sociaux</Label>
                    <Textarea
                      id="amb-social-links"
                      value={formState.social_links_raw}
                      onChange={(event) => updateField('social_links_raw', event.target.value)}
                      placeholder="https://instagram.com/votreprofil&#10;https://tiktok.com/@votreprofil"
                      rows={4}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ajoutez un lien par ligne (ou séparés par des virgules). {socialLinksPreview.length} lien(s)
                      detecte(s).
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="amb-message">Message / motivation (optionnel)</Label>
                    <Textarea
                      id="amb-message"
                      value={formState.message}
                      onChange={(event) => updateField('message', event.target.value)}
                      rows={4}
                      placeholder="Expliquez votre audience et vos motivations."
                    />
                  </div>

                  <div className="rounded-md bg-gray-100 p-3 text-sm text-gray-700">
                    En soumettant ce formulaire, vous confirmez que vos informations sont exactes. La candidature est
                    analysée par l’administration avant activation.
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-red-600 hover:bg-red-700 disabled:opacity-60"
                  >
                    {submitting ? 'Soumission en cours...' : 'Envoyer ma candidature'}
                  </Button>

                  {submissionState?.id && (
                    <div className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800">
                      Candidature enregistree (id: <strong>{submissionState.id}</strong>) - statut:{' '}
                      <strong>{submissionState.status || 'pending'}</strong>.
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default AmbassadorProgramPage;
